import Button from 'components/Form/Button';
import Input from 'components/Form/Input';
import { CoreContext } from 'context/CoreContext';
import { useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { POST } from 'services/api';
import styled, { keyframes } from 'styled-components';
import { Title } from 'ui/styled';
import { CompanyButtonContainer } from './styled';

// ====== ANIMAÇÃO & ESTILOS ======
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ModalWrapper = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 10px;
`;

const ModalContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 500px;
  animation: ${fadeIn} 0.3s ease-out;
  padding: 20px;
`;

const HeaderInfo = styled.div`
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
`;

const BancoTitle = styled(Title)`
  color: #333;
  font-size: 1.5em;
  font-weight: 600;
  margin-bottom: 5px;
`;

const ReadOnlyField = styled.div`
  width: 100%;
  padding: 12px;
  margin: 8px 0 16px;
  border: 1px solid #e0e0e0;
  background-color: #f9f9f9;
  border-radius: 6px;
  font-size: 1em;
  color: #444;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
  font-size: 0.95em;
`;

const ActionButtonContainer = styled(CompanyButtonContainer)`
  margin-top: 25px;
  justify-content: flex-end;
  gap: 10px;
`;

// ====== FUNÇÃO PARA VALIDAR DATA/HORA ======
function validarDataHoraUsuario(inputDataHora) {
  if (!inputDataHora) return null;
  const data = new Date(inputDataHora);
  const diaSemana = data.getDay();
  const hora = data.getHours();
  const minuto = data.getMinutes();
  const agora = new Date();
  // Bloqueia datas anteriores ao momento atual
  if (data < agora) {
    return null;
  }
  // Bloqueia final de semana
  if (diaSemana === 0 || diaSemana === 6) {
    return null;
  }
  // Bloqueia horários antes das 08:00 ou depois das 16:55
  if (hora < 8 || (hora === 16 && minuto > 55) || hora > 16) {
    return null;
  }
  return inputDataHora;
}
// ====== COMPONENTE ======
export default function AgendaProposta({ dados, reload, onClose }) {
  const [loading, setLoading] = useState(false);
  const { setModal } = useContext(CoreContext);
  const [dataContato, setDataContato] = useState('');

  const close = () => setModal(null);

  // ====== DADOS DA PROPOSTA ======

  const proposalData = dados;

  const dataAgendada = dados.data_contato_cliente;

  const propostaId = dados.id;
  const statusAtual = (dados.status || '').toLowerCase();
  const etapaAtual = 'conhecimento compra';

  // ====== LÓGICA POR ETAPA ======
  const config = useMemo(() => {
    const etapaLower = etapaAtual.toLowerCase();
    if (etapaLower.includes('fatura cliente')) {
      return {
        titulo: 'Fatura Cliente',
        tipoAcao: 'fatura_cliente',
        statusRequerido: 'pendente',
        payload: { fatura_vendedor: 'nao', curadoria_analise_bot: null },
        mostraDataHora: false,
      };
    }
    if (etapaLower.includes('conhecimento compra')) {
      return {
        titulo: 'Conhecimento de Compra',
        tipoAcao: 'conhecimento_compra',
        statusRequerido: 'autorizado',
        payload: { compra_ciente_enviado: false, curadoria_analise_bot: null },
        mostraDataHora: true,
        campoDataHora: 'data_contato_cliente',
      };
    }
    if (etapaLower.includes('confirmacao vendedor')) {
      return {
        titulo: 'Confirmação do Vendedor',
        tipoAcao: 'confirmacao_vendedor',
        statusRequerido: 'aprovado',
        payload: { ciencia_vendedor: null, curadoria_analise_bot: null },
        mostraDataHora: false,
      };
    }
    return {
      titulo: 'Ação Não Suportada',
      tipoAcao: null,
      statusRequerido: null,
      payload: {},
      mostraDataHora: false,
    };
  }, [etapaAtual]);
  const {
    titulo,
    tipoAcao,
    statusRequerido,
    payload,
    mostraDataHora,
    campoDataHora,
  } = config;
  const podeConfirmar =
    statusAtual === statusRequerido &&
    (!mostraDataHora || dataContato.trim() !== '');
  if (!loading) {
    onClose?.();
  }

  // ====== ENVIO COM VALIDAÇÃO DE DATA E AJUSTE DE 5 MIN ======
  const save = async () => {
    if (!proposalData?.id) {
      toast.error('Erro: ID da proposta não encontrado.');
      return;
    }
    if (!tipoAcao) {
      toast.error('Ação não suportada para esta etapa.');
      return;
    }
    const dataPayload = { ...payload };
    if (mostraDataHora && campoDataHora) {
      if (!dataContato.trim()) {
        toast.error('Preencha a data e hora do contato com o cliente.');
        return;
      }
      let dataValida = validarDataHoraUsuario(dataContato);
      if (!dataValida) {
        toast.error(
          'Data e hora inválida. O contato deve ser de segunda a sexta entre 08:00 e 16:55.'
        );
        return;
      }
      // Ajuste: adiciona 5 minutos antes de enviar
      const dataObj = new Date(dataValida);
      dataObj.setMinutes(dataObj.getMinutes() + 5);
      dataPayload[campoDataHora] = dataObj.toISOString();
    }
    const payloadFinal = {
      propostaId: proposalData.id,
      tipo_acao: tipoAcao,
      attStatus: 'Analisado',
      data: dataPayload,
    };
    setLoading(true);
    try {
      const result = await POST(
        '/financing-proposal/curadoria/reagendar',
        payloadFinal,
        true
      );
      if (result?.status === 'ok' || result?.success) {
        toast.success(`${titulo} enviada com sucesso!`);
        await reload?.();
        close();
      } else {
        toast.error(
          `Erro: ${result?.erro || result?.message || 'Falha inesperada'}`
        );
      }
    } catch (error) {
      console.error(`[Erro na API - ${titulo}]:`, error);
      toast.error('Falha na comunicação com o servidor.');
    } finally {
      setLoading(false);
    }
  };
  const minDataInput = new Date().toISOString().slice(0, 16);
  return createPortal(
    <ModalWrapper onClick={close}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <HeaderInfo>
          <BancoTitle>{titulo}</BancoTitle>
          <p style={{ margin: '8px 0', color: '#555' }}>
            Proposta: <strong>{propostaId}</strong>
          </p>
        </HeaderInfo>
        <div>
          <Label>Status Atual</Label>
          <ReadOnlyField>
            <strong>{statusAtual}</strong>
          </ReadOnlyField>
          {mostraDataHora && (
            <>
              <Label>
                Agendado para {dataAgendada} para contato com o Cliente,
                reagendar para:
              </Label>
              <Input
                type="datetime-local"
                value={dataContato}
                onChange={(e) => setDataContato(e.target.value)}
                style={{ marginBottom: '16px' }}
                required
                min={minDataInput}
              />
            </>
          )}
          {!podeConfirmar && statusRequerido && (
            <div
              style={{
                color: '#d32f2f',
                fontSize: '0.9em',
                marginTop: '16px',
                padding: '12px',
                background: '#ffebee',
                borderRadius: '6px',
                border: '1px solid #ffcdd2',
              }}
            >
              <strong>Atenção:</strong>{' '}
              {statusAtual !== statusRequerido ? (
                <>
                  O status precisa estar como <b>{statusRequerido}</b> para
                  enviar.
                </>
              ) : (
                <>Preencha a data e hora do contato.</>
              )}
            </div>
          )}
        </div>
        {/* ATENÇÃO CENTRALIZADO + TEXTO ABAIXO */}
        <div
          style={{
            marginTop: '16px',
            padding: '14px',
            background: '#ffebee',
            border: '1px solid #ffcdd2',
            borderRadius: '6px',
            textAlign: 'center',
            color: '#d32f2f',
            fontSize: '0.9em',
            lineHeight: '1.6',
          }}
        >
          <div style={{ fontWeight: '700', fontSize: '1em' }}>Atenção</div>
          <div style={{ marginTop: '6px', fontWeight: '500' }}>
            {etapaAtual.toLowerCase().includes('parecer de acesso') ? (
              <>Para o envio da Sol: a cada 5 min (8h–17h, seg–sex)</>
            ) : etapaAtual.toLowerCase().includes('protocolo') ? (
              <>Para o envio da Sol: a cada 5 min (8h–17h, seg–sex)</>
            ) : etapaAtual.toLowerCase().includes('instalacao') ? (
              <>Para o envio da Sol: hoje às 9:15 (seg–sex)</>
            ) : etapaAtual.toLowerCase().includes('conexao') ? (
              <>Para o envio da Sol: hoje às 9:30 (seg–sex)</>
            ) : etapaAtual.toLowerCase().includes('primeira fatura') ? (
              <>Para o envio da Sol: hoje às 10:00 (seg–sex)</>
            ) : etapaAtual.toLowerCase().includes('fatura cliente') ? (
              <>Para o envio da Sol: a cada 5 min (8h–17h, seg–sex)</>
            ) : etapaAtual.toLowerCase().includes('confirmacao vendedor') ? (
              <>Para o envio da Sol: a cada 5 min (8h–17h, seg–sex)</>
            ) : etapaAtual.toLowerCase().includes('conhecimento compra') ? (
              <>Para o envio da Sol: preencha data e hora (8h–16h55, seg–sex)</>
            ) : (
              <>Aguardando ação na etapa atual</>
            )}
          </div>
        </div>
        <ActionButtonContainer>
          <Button
            onClick={close}
            color="lightgrey"
            border
            nospace
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            color="blue"
            loading={loading}
            onClick={() => {
              if (statusAtual !== statusRequerido) {
                toast.error(
                  'Não é possível enviar: o status precisa estar correto.'
                );
                return;
              }
              if (tipoAcao) save();
            }}
            nospace
            disabled={loading || !tipoAcao}
          >
            Confirmar
          </Button>
        </ActionButtonContainer>
      </ModalContainer>
    </ModalWrapper>,
    document.body
  );
}
